/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import { Button, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import { postApi } from "redux/action/api_tbeams";
import { formatNumber, monthAgo } from "redux/action/api";
import { useToasts } from "helper/ToastAdapter";
import moment from "moment";
import { useSelector } from "react-redux";
import SearchBar from "components/SearchBar";
import ApprovalHistoryModal from "components/ApprovalHistoryModal";

export default function ReleaseHistoryGenericTab() {
  const { addToast } = useToasts();
  const user = useSelector((state) => state.auth.user);

  const [isLoading, setIsLoading] = useState(false);
  const [allData, setAllData] = useState([]); // Store all fetched data
  const [filteredData, setFilteredData] = useState([]); // Store filtered data for display
  const [filters, setFilters] = useState({
    search: "",
    amount: "",
    mda_name: "",
    date_from: monthAgo,
    date_to: moment().format("YYYY-MM-DD"),
  });
  const [filter] = useState("All");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);

  // Fetch all data on component mount
  const getApprovalCollection = useCallback(() => {
    setIsLoading(true);
    postApi(`post_approval_collection`, {
      form: {
        query_type: "by_filter",
        filter,
        type: "release",
        date_from: filters.date_from,
        date_to: filters.date_to,
      },
    })
      .then((data) => {
        console.log("data==============", data);
        if (data.result) {
          setAllData(data.result);
          setFilteredData(data.result); // Initially show all data
        }
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        addToast("Failed to fetch release history", {
          appearance: "error",
          autoDismiss: true,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter, filters.date_from, filters.date_to]);

  useEffect(() => {
    getApprovalCollection();
  }, [getApprovalCollection]);

  const handleFilterChange = ({ target: { name, value } }) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Client-side search/filter function
  const handleSearch = (e) => {
    e?.preventDefault();

    let results = [...allData];

    // Filter by search text (approval_no or mda_name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(
        (item) =>
          item.approval_no?.toLowerCase().includes(searchLower) ||
          item.mda_name?.toLowerCase().includes(searchLower),
      );
    }

    // Filter by MDA name
    if (filters.mda_name) {
      const mdaLower = filters.mda_name.toLowerCase();
      results = results.filter((item) =>
        item.mda_name?.toLowerCase().includes(mdaLower),
      );
    }

    // Filter by amount (exact match)
    if (filters.amount) {
      const searchAmount = parseFloat(filters.amount);
      results = results.filter(
        (item) => parseFloat(item.amount) === searchAmount,
      );
    }

    // Filter by date range
    if (filters.date_from || filters.date_to) {
      results = results.filter((item) => {
        const itemDate = moment(item.approval_date || item.collection_date);
        const fromDate = filters.date_from ? moment(filters.date_from) : null;
        const toDate = filters.date_to ? moment(filters.date_to) : null;

        if (fromDate && toDate) {
          return itemDate.isBetween(fromDate, toDate, "day", "[]");
        } else if (fromDate) {
          return itemDate.isSameOrAfter(fromDate, "day");
        } else if (toDate) {
          return itemDate.isSameOrBefore(toDate, "day");
        }
        return true;
      });
    }

    setFilteredData(results);
  };

  const handleViewAudit = (release) => {
    setSelectedRelease(release);
    setIsModalOpen(true);
  };

  const toggleModal = () => {
    setIsModalOpen(false);
    setSelectedRelease(null);
  };

  const styles = {
    fontSize: 14,
    padding: "8px",
    verticalAlign: "middle",
  };

  const bodyStyle = {
    fontSize: 13,
    padding: "8px",
  };

  return (
    <>
      <div style={{ padding: "20px 0" }}>
        <h6 style={{ marginBottom: "15px", color: "#333", fontWeight: 600 }}>
          Search Release History
        </h6>
        <form onSubmit={handleSearch}>
          <Row style={{ marginBottom: "20px" }}>
            <Col md={4}>
              <Label style={{ fontWeight: 500, fontSize: "14px" }}>
                Search
              </Label>
              <SearchBar
                placeholder="Search by Approval No or MDA"
                filterText={filters.search}
                onFilterTextChange={(e) =>
                  handleFilterChange({
                    target: { name: "search", value: e.target.value },
                  })
                }
              />
            </Col>
            <Col md={2}>
              <Label style={{ fontWeight: 500, fontSize: "14px" }}>
                Amount
              </Label>
              <Input
                type="number"
                name="amount"
                placeholder="Enter amount"
                value={filters.amount}
                onChange={handleFilterChange}
                style={{ fontSize: "14px" }}
              />
            </Col>
            <Col md={2}>
              <Label style={{ fontWeight: 500, fontSize: "14px" }}>
                Date From
              </Label>
              <Input
                type="date"
                name="date_from"
                value={filters.date_from}
                onChange={handleFilterChange}
                style={{ fontSize: "14px" }}
              />
            </Col>
            <Col md={2}>
              <Label style={{ fontWeight: 500, fontSize: "14px" }}>
                Date To
              </Label>
              <Input
                type="date"
                name="date_to"
                value={filters.date_to}
                onChange={handleFilterChange}
                style={{ fontSize: "14px" }}
              />
            </Col>
            <Col md={2} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                color="primary"
                onClick={handleSearch}
                style={{ width: "100%", fontWeight: 600 }}
              >
                <i className="fas fa-search" style={{ marginRight: "5px" }}></i>
                Search
              </Button>
            </Col>
          </Row>
        </form>
      </div>

      <div
        className="mt-3"
        style={{
          width: "100%",
          height: 500,
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
        }}
      >
        <Table
          bordered
          hover
          style={{ width: "100%", marginBottom: 0, backgroundColor: "#fff" }}
        >
          <thead
            style={{
              backgroundColor: "#f8f9fa",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <tr>
              <th style={styles}>Action</th>
              <th style={styles}>Approval No</th>
              <th style={styles}>Amount</th>
              <th style={styles}>MDA Name</th>
              <th style={styles}>Collection Date</th>
              <th style={styles}>Approval Date</th>
              <th style={styles}>Status</th>
              <th style={styles}>Approved By</th>
            </tr>
          </thead>
          {isLoading ? (
            <tbody>
              <tr>
                <td
                  colSpan={8}
                  className="text-center"
                  style={{ padding: "40px" }}
                >
                  <Spinner color="primary" />
                  <div className="mt-2" style={{ color: "#666" }}>
                    Loading history...
                  </div>
                </td>
              </tr>
            </tbody>
          ) : filteredData.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={8}
                  className="text-center"
                  style={{ padding: "40px", color: "#999" }}
                >
                  <i
                    className="fas fa-inbox"
                    style={{ fontSize: "48px", marginBottom: "10px" }}
                  ></i>
                  <div>No release history found</div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {filteredData.map((item, idx) => (
                <tr key={idx} style={{ cursor: "pointer" }}>
                  <td style={bodyStyle}>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => handleViewAudit(item)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {/* <IoMdEye size={16} /> */}
                      History
                    </Button>
                  </td>
                  <td style={bodyStyle}>{item.approval_no}</td>
                  <td
                    style={{
                      ...bodyStyle,
                      textAlign: "right",
                      fontWeight: "500",
                    }}
                  >
                    {formatNumber(item.amount)}
                  </td>
                  <td style={bodyStyle}>{item.mda_name}</td>
                  <td style={bodyStyle}>{item.collection_date}</td>
                  <td style={bodyStyle}>{item.approval_date}</td>
                  <td style={bodyStyle}>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: "4px",
                        backgroundColor: item.approval_status?.includes(
                          "Released",
                        )
                          ? "#d4edda"
                          : item.approval_status?.includes("Returned")
                            ? "#fff3cd"
                            : "#d1ecf1",
                        color: item.approval_status?.includes("Released")
                          ? "#155724"
                          : item.approval_status?.includes("Returned")
                            ? "#856404"
                            : "#0c5460",
                        fontWeight: "500",
                        fontSize: "11px",
                      }}
                    >
                      {item.approval_status}
                    </span>
                  </td>
                  <td style={bodyStyle}>{item.approved_by}</td>
                </tr>
              ))}
            </tbody>
          )}
        </Table>
      </div>

      {!isLoading && filteredData.length > 0 && (
        <div
          className="mt-3"
          style={{
            padding: "10px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            textAlign: "center",
            fontSize: "14px",
            color: "#666",
          }}
        >
          <strong>{filteredData.length}</strong> record(s) found
          {filteredData.length !== allData.length && (
            <span style={{ marginLeft: "10px", color: "#999" }}>
              (filtered from {allData.length} total)
            </span>
          )}
        </div>
      )}

      {/* Approval History Modal */}
      <ApprovalHistoryModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        approvalNo={selectedRelease?.approval_no}
        releaseData={selectedRelease}
      />
    </>
  );
}
